import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useSession } from '../../lib/store';
import { Card, Empty, Notice, PageHeading, Spinner } from '../../components/Shell';
import { longDate } from '../../lib/format';
import type { TrainingVideo } from '../../lib/types';

/**
 * The client's video library, lessons and pet clips alike. Nothing here is a
 * link that can be forwarded: a play button asks the API for a fresh watch URL each time, and
 * that URL stops working in minutes.
 */
export function ClientVideos() {
  const session = useSession();
  const tenantId = session.tenant?.tenantId;
  const businessName = session.tenant?.businessName ?? 'Your sitter';

  const [videos, setVideos] = useState<TrainingVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [playingId, setPlayingId] = useState<string | null>(null);
  const [watchUrl, setWatchUrl] = useState<string | null>(null);
  const [starting, setStarting] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    api<{ videos: TrainingVideo[] }>(`/tenants/${tenantId}/videos`)
      .then((result) => setVideos(result.videos))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Videos could not be loaded.'))
      .finally(() => setLoading(false));
  }, [tenantId]);

  const play = (videoId: string) => {
    if (!tenantId) return;
    setStarting(videoId);
    setError(null);

    void api<{ watchUrl: string }>(`/tenants/${tenantId}/videos/${videoId}/watch`, { method: 'POST' })
      .then((result) => {
        setPlayingId(videoId);
        setWatchUrl(result.watchUrl);
      })
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : 'This video could not be opened right now.')
      )
      .finally(() => setStarting(null));
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <Spinner label="Loading videos" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-5 pb-24 pt-12">
      <PageHeading
        eyebrow={session.tenant?.businessName}
        title="Videos"
        lead={`Clips ${businessName} has shared with you. Nobody else can open them.`}
      />

      {error && (
        <div className="mb-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      {videos.length === 0 ? (
        <Empty
          title="Nothing here yet"
          body={`When ${businessName} records something worth keeping, it will be waiting here for you.`}
        />
      ) : (
        <ul className="space-y-4">
          {videos.map((video) => (
            <li key={video.videoId}>
              <Card className="p-6">
                <div className="min-w-0">
                  <p className="min-w-0 truncate font-display text-lg font-semibold">{video.title}</p>
                  <p className="typed text-xs text-[color:var(--color-ink-soft)]">Added {longDate(video.createdAt)}</p>
                  {video.description && (
                    <p className="mt-2 min-w-0 text-sm text-[color:var(--color-ink-soft)]">{video.description}</p>
                  )}
                </div>

                <div className="mt-4">
                  {playingId === video.videoId && watchUrl ? (
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <video
                      controls
                      autoPlay
                      src={watchUrl}
                      className="w-full rounded-sm border border-[color:var(--color-rule)]"
                    />
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => play(video.videoId)}
                      disabled={starting === video.videoId}
                    >
                      {starting === video.videoId ? 'Opening' : 'Play'}
                    </button>
                  )}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
